import { useEffect, useRef } from "react";

const projects = [
  {
     id: "preply",
  title: "The Nail Salon",
  subtitle: "",
  blurb:
    "A VICE editorial series on self-care, identity and self-expression. Three interviews became 40 social assets. 2M+ views across channels.",
  tag: "01",
  tone: "cool",
  media: "/images/TheNailsalon-thumbnail.png",
  mediaOpacity: 1.0,
  mediaBlend: "normal",
  mediaFilter: "none",
  },
  {
    id: "vice",
    title: "JBL Music Academy",
    subtitle: "",
    blurb:
      "A global content campaign for JBL's Music Academy in Amsterdam: 40 emerging artists, 9 markets, local talent for each. Featuring Martin Garrix and Virgil van Dijk.",
    tag: "02",
    tone: "cool",
    media: "/images/Jbl-case.png",
    mediaOpacity: 1.0,
    mediaPosition: "top",
    mediaBlend: "normal",
    mediaFilter: "saturate(1.15) contrast(1.02)",
  },
  {
    id: "spec",
    title: "Vice · Red Bull · Rakuten",
    subtitle: "",
    blurb:
      "On and offline editing and post-production across four series. Distributed on Prime Video, Red Bull TV, and Rakuten TV. Peabody Award-winning work.",
    tag: "03",
    tone: "cool",
    flag: "",
    media: "https://customer-assets.emergentagent.com/job_frame-work-9/artifacts/8mtdlss5_docu%20block%20final%20v2.png",
    mediaOpacity: 0.8,
    mediaBlend: "normal",
    mediaFilter: "none",
    mediaPosition: "center",
    cleanImage: true,
    hideTimecode: true,
  },
];

const toneStyles = {
  warm: {
    backgroundImage:
      "radial-gradient(ellipse at 30% 30%, rgba(218,160,90,0.35), transparent 60%), radial-gradient(ellipse at 75% 75%, rgba(120,60,40,0.45), transparent 55%), linear-gradient(160deg,#3a2b1f,#1a120c)",
  },
  cool: {
    backgroundImage:
      "radial-gradient(ellipse at 60% 30%, rgba(120,150,160,0.35), transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(40,60,70,0.55), transparent 55%), linear-gradient(180deg,#1f2528,#0e1214)",
  },
  earth: {
    backgroundImage:
      "radial-gradient(ellipse at 40% 20%, rgba(180,140,90,0.32), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(90,80,60,0.55), transparent 55%), linear-gradient(175deg,#2d2820,#14110c)",
  },
};

function StillCard({ p, className, aspect, style }) {
  const num = Number(p.tag.replace(/\D/g, ""));
  return (
    <figure
      data-testid={`work-card-${p.id}`}
      className={`reveal relative overflow-hidden ${className || ""}`}
      style={{ margin: 0, ...(style || {}) }}
    >
      <div
        className="placeholder-art"
        style={{
          width: "100%",
          aspectRatio: aspect || "16/10",
          position: "relative",
          ...(p.cleanImage ? { backgroundImage: "none", backgroundColor: "#EEE7D6" } : toneStyles[p.tone]),
        }}
      >
        {p.media && (
          <img
            src={p.media}
            alt={`${p.title} — asset reel`}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: p.mediaPosition || "center",
              opacity: p.mediaOpacity ?? 0.4,
              mixBlendMode: p.mediaBlend || "luminosity",
              filter: p.mediaFilter || "saturate(0.9) contrast(0.95)",
              pointerEvents: "none",
            }}
          />
        )}
        {!p.cleanImage && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(10,8,6,0.15) 0%, rgba(10,8,6,0) 40%, rgba(10,8,6,0.35) 100%)",
              pointerEvents: "none",
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            top: 18,
            left: 18,
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            color: p.cleanImage ? "rgba(30,28,25,0.72)" : "rgba(244,239,228,0.8)",
            zIndex: 2,
          }}
        >
          [ {p.tag} ]
        </div>
        </div>
      <figcaption style={{ marginTop: 18, display: "grid", gap: 10 }}>
        {p.flag ? (
          <div
            className="font-mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--ink-muted)",
              marginBottom: 2,
            }}
          >
            {p.flag}
          </div>
        ) : null}
        <div className="font-serif" style={{ fontSize: 26, lineHeight: 1.1, color: "var(--ink)" }}>
          {p.title}
          {p.subtitle ? <span style={{ color: "var(--ink-muted)" }}> — {p.subtitle}</span> : null}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-muted)", maxWidth: 680 }}>
         {p.blurb}
       </div>
      </figcaption>
    </figure>
  );
}

const desktopCases = [
  {
    ...projects[0],
    kicker: "",
    stats: [{ v: "40", k: "assets" }, { v: "2M+", k: "views" }],
    tagline: "",
  },
  {
    ...projects[1],
    kicker: "",
    stats: [{ v: "40", k: "artists" }, { v: "9", k: "markets" }],
    tagline: "",
  },
  {
    ...projects[2],
    kicker: "",
    stats: [{ v: "14", k: "episodes" }, { v: "3", k: "platforms" }],
    tagline: "",
  },
];

export default function Work() {
  const ref = useRef(null);
  useEffect(() => {
    const nodes = ref.current?.querySelectorAll(".reveal") || [];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add("in"));
    }, { threshold: 0.1 });
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} id="work" data-testid="work-section" className="work-section" style={{ padding: "140px 0 28px" }}>
      <div className="mx-auto pad-x-40" style={{ maxWidth: 1440, padding: "0 40px" }}>
        <div className="work-desktop">
          <div className="work-cases">
            {desktopCases.map((p, idx) => (
              <article key={p.id} className={`work-case reveal ${idx % 2 === 1 ? "work-case-reverse" : ""}`}>
                <div className={`work-case-media ${p.cleanImage ? "work-case-media-clean" : ""}`}>
                  <img src={p.media} alt={`${p.title} — case still`} style={{ objectPosition: p.mediaPosition || "center", objectFit: p.id === "spec" ? "contain" : "cover" }} />
                  <div className="work-case-overlay" />
                  <span className="work-case-index mono">[{p.tag}]</span>
                </div>
                <div className="work-case-body">
                  <div className="work-case-headline">
                    <h3 className="work-case-title font-serif">{p.title}</h3>
                    <p className="work-case-copy">{p.blurb}</p>
                  </div>
                  <div className="work-case-foot">
                    <div className="work-case-stats">
                      {p.stats.map((m) => (
                        <span key={`${p.id}-${m.k}`} className="work-case-stat">
                          <span className="work-case-stat-val">{m.v}</span>
                          <span className="work-case-stat-key">{m.k}</span>
                        </span>
                      ))}
                    </div>
                    {p.tagline ? <span className="work-case-tag mono">{p.tagline}</span> : null}
                  </div>
                </div>
             </article>
            ))}
          </div>
        </div>

        <div className="work-mobile">
          <div className="work-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "56px 48px" }}>
            <StillCard p={projects[0]} className="delay-1" aspect="16/10" />
            <StillCard p={projects[1]} className="delay-2" aspect="16/10" />
          </div>

          <section
            data-testid="editorial-strip"
            className="reveal delay-3 editorial-strip"
            style={{ marginTop: 32, marginLeft: 0, marginRight: "auto", width: "70%", maxWidth: 980 }}
          >
            <div style={{ width: "100%", overflow: "hidden", borderRadius: 6 }}>
              <img
                src={projects[2].media}
                alt="Vice, Red Bull and Rakuten series posters"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  opacity: projects[2].mediaOpacity ?? 0.75,
                }}
              />
            </div>

            <div style={{ marginTop: 18, display: "grid", gap: 10, textAlign: "center" }}>
              <div className="font-serif" style={{ fontSize: 22, lineHeight: 1.15, color: "var(--ink)" }}>
                {projects[2].title}
              </div>
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "var(--ink-muted)",
                  maxWidth: 720,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {projects[2].blurb}
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
